import { Component, inject, Input, OnInit } from '@angular/core';
import { NgbActiveModal, NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { ButtonThemes, ButtonSizes, ButtonComponent } from '../button/button.component';
import { CommonModule } from '@angular/common';
import { environment } from '@environments/environment';
import { CustomerAddressPayload } from '@shared/models/customer-address/customer-address.model';
import { CustomerAddressService } from '@shared/services/customer-address/customer-address.service';
import { SelectComponent } from '../select/select.component';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { CustomerStore } from '@shared/stores/customer/customer.store';
import { UserStore } from '@shared/stores/user/user.store';
import { Customer } from '@shared/models/customer/customer.model';
import { User } from '@shared/models/user/user.model';
import { CustomerService } from '@shared/services/customer/customer.service';
import { ToastService } from '@shared/services/toast/toast.service';
import { ERR_MESSAGES } from '@shared/constants/messages.constants';
import { TOAST_TYPE } from '@shared/constants/toast.constants';
import { OrderRequestResponseModalComponent } from '../order-request-response-modal/order-request-response-modal.component';
import { DigitOnlyDirective } from './digit-only.directive';

@Component({
  selector: 'avenir-order-request-modal',
  standalone: true,
  imports: [
    CommonModule,
    ButtonComponent,
    SelectComponent,
    ReactiveFormsModule,
    DigitOnlyDirective
  ],
  templateUrl: './order-request-modal.component.html',
  styleUrl: './order-request-modal.component.scss'
})
export class OrderRequestModalComponent implements OnInit {
  readonly BUTTON_THEME = ButtonThemes;
  readonly BUTTON_SIZE = ButtonSizes;
  @Input() customerId: number = 0;
  public modalService = inject(NgbModal);
  private modal = inject(NgbActiveModal);
  private customerAddressService = inject(CustomerAddressService);

  public orderForm!: FormGroup;

  public locations: any;
  user!: User;
  customers: Customer[] = [];
  customer!: Customer;

  isTankLevelValid: boolean = true;
  isLocationValid: boolean = true;

  orderPropaneLabel = $localize`:@@orderPropaneLabel:Order Propane`
  btnCancelLabel = $localize`:@@cancel:Cancel`
  btnSubmitLabel = $localize`:@@submitOrder:Submit Order`
  tankLevelEstimateSubtext = $localize`:@@tankLevelEstimateSubtext:If you do not have a tank monitor please enter an estimate.`
  tankLevelError = $localize`:@@tankLevelError:Tank level must be between 0% and 80%.`

  constructor(
    private userStore: UserStore,
    protected customerStore: CustomerStore,
    private customerService: CustomerService,
    private toastService: ToastService,
    private fb: FormBuilder
  ) { }

  ngOnInit(): void {
    this.userStore.user$.subscribe(user => {
      this.user = this.userStore.getUser();
      this.customers = this.user.customers;
      const customer = this.customers.find(customer => customer.customerId == this.customerId);
      if (customer) {
        this.customer = customer;
      }
    })

    this.orderForm = this.fb.group({
      clientId: [environment.CLIENT_ID],
      customerId: [this.customerId],
      userId: [this.user.userId],
      customerAddressId: [0],
      currentTankLevel: [30, [Validators.required, Validators.min(0), Validators.max(80)]],
      deliveryInstructions: ['']
    });

    const customerAddressPayload: CustomerAddressPayload = {
      clientId: environment.CLIENT_ID,
      mode: "CUSTOMER",
      customerId: this.customerId,
      pageSize: 9999,
      pageNumber: 1,
      sort: "shipToCode:asc",
      filter: {
        searchParam: "",
        siteId: "",
        siteName: "",
        streetAddress: "",
        city: "",
        state: "",
        zipCode: "",
        groupFilter: "",
        status: "ACTIVE",
        typeFilter: "SHIPPING",
        withTanks: true
      }
    };
    this.customerAddressService.getCustomerAddresses(customerAddressPayload).subscribe((res) => {
      // Map the response to create an array of provinces
      let temp1 = res.customerAddresses;
      this.locations = temp1.map((obj) => {
        return {
          value: parseInt(obj.customerAddressId?.toString() ?? '0'),
          label: obj.shipToCode + ' - ' + obj.street + ' ' + obj.city + ' ' + obj.state + ' ' + obj.zipCode,
        }
      });
      if (this.locations.length > 0) {
        this.isLocationValid = true;
        this.orderForm.patchValue({ customerAddressId: this.locations[0].value });
      }
      else{
        this.isLocationValid = false;
      }
    });
  }

  onLocationSelected(selected: any) {
    this.orderForm.patchValue({ customerAddressId: selected.value });
  }

  submitOrderRequest() {
    this.customerService.submitOrderRequest(this.orderForm.value).subscribe({
      next: (res: any) => {
        this.modal.dismiss();
        const modal = this.modalService.open(OrderRequestResponseModalComponent, { size: 'md' });
        const modalRef = modal.componentInstance as OrderRequestResponseModalComponent;
        modalRef.userName = this.user!.fullname!;
        modalRef.accountName = this.customer!.name!;
        modalRef.accountNumber = this.customer!.legacyId!;
        modalRef.location = this.locations.find((location: any) => location.value == this.orderForm.value['customerAddressId'])?.label || 'All';
        modalRef.currentTankLevel = this.orderForm.value['currentTankLevel'];
        modalRef.deliveryInstructions = this.orderForm.value['deliveryInstructions'];
      },
      error: (error: any) => {
        if (error.status != 500 && error.status != 401 && error.status != 403) {
          this.toastService.show(ERR_MESSAGES.GENERIC + error.error.error, TOAST_TYPE.ERROR, 3);
        }
      }
    });
  }

  onTankLevelChange(event: any) {
    let value = parseInt(event.target.value);
    if (isNaN(value) || value < 0 || value > 80) {
      if (isNaN(value) || value < 0){
        this.orderForm.patchValue({ currentTankLevel: 0 });
      }
      else if (value > 80){
        this.orderForm.patchValue({ currentTankLevel: 80 });
      }
      const tankLevelInput = document.getElementById('tankLevelInput') as HTMLInputElement;
      tankLevelInput.select();
    }
    else{
      this.isTankLevelValid = true;
    }
  }
}
